import {
  WhmAlert,
  WhmAvatar,
  WhmButton,
  WhmCheckbox,
  WhmDivider,
  WhmFlexDataGrid,
  WhmFormControlLabel,
  WhmIconButton,
  WhmLoadingButton,
  WhmMenuItem,
  WhmStack,
  whmTheme,
  WhmTooltip,
  WhmTypography,
} from "@securitize/reactjs-whm";
import { InfoCard } from "../../shared/InfoCard";
import { Translate, TranslateHtml } from "../../../Translation/Translate";
import { I18nContext, translationKeys } from "../../../../contexts/I18nContext";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import useUserInfo from "../../../../hooks/useUserInfo";
import {
  entityTypesTranslations,
  TranslationKey,
} from "../../../../contexts/I18nContext/translationKeys";
import { iconSmall } from "../../../shared/iconThemes";
import { ReactComponent as InfoOutlined } from "@securitize/assets-sec-ui/dist/icons/svg/info.svg";
import { ReactComponent as Warning } from "@securitize/assets-sec-ui/dist/icons/svg/advise.svg";
import { ReactComponent as IndividualIcon } from "@securitize/assets-sec-ui/dist/icons/svg/individual-profile.svg";
import { ReactComponent as EntityIcon } from "@securitize/assets-sec-ui/dist/icons/svg/entity.svg";
import { ReactComponent as DeleteIcon } from "@securitize/assets-sec-ui/dist/icons/svg/delete.svg";
import { ReactComponent as Dots } from "@securitize/assets-sec-ui/dist/icons/svg/more-options.svg";
import StyledCard from "../../../Portfolio/components/StyledCard";
import AddIndividualDialog from "./AddIndividualDialog";
import EmptyKeyPartiesOverlay from "./EmptyKeyPartiesOverlay";
import { AddMemberButton } from "./AddMemberButton";
import { showsAsEnhanced } from "./utils";
import { useQueryClient } from "@tanstack/react-query";
import CompleteIndividualDialog from "./CompleteIndividualDialog";
import { useUpdateKeyPartyMutation } from "../../../../data/useProfile";
import { SnackbarContext } from "../../../shared/Snackbar";
import ErrorSnackbar from "../../../shared/ErrorSnackbar";
import FlexMenu from "../../../shared/FlexMenu";
import { KeyParty } from "../../../../data/dtos/get-key-party.dto";
import DeleteKeyPartyDialog from "./DeleteKeyPartyDialog";
import AddEntityDialog from "./AddEntityDialog";
import CompleteEntityDialog from "./CompleteEntityDialog";
import { useGetOnboardingData } from "../../../../data/usePortfolio";
import {
  isItemResolved,
  markPotentiallyResolved,
} from "../../../../utils/updatesRequiredResolution";
import { invalidateKeyPartiesQueries } from "../../../../utils/queryInvalidation";

function editableFieldsSnapshot(keyParty: KeyParty): string {
  const {
    individualName,
    email,
    taxCountryCode,
    taxId,
    address,
    birthDate,
    legalName,
    entityType,
    uboOwning,
    isLegalSigner,
  } = keyParty;
  return JSON.stringify({
    individualName,
    email,
    taxCountryCode,
    taxId,
    address,
    birthDate,
    legalName,
    entityType,
    uboOwning,
    isLegalSigner,
  });
}

export default function KeyPartiesSection({
  keyParties,
  isRefreshing,
  isReadOnly = false,
  canDelete = true,
}: {
  keyParties: KeyParty[];
  isRefreshing: boolean;
  isReadOnly?: boolean;
  canDelete?: boolean;
}) {
  const user = useUserInfo();
  const enhanced = showsAsEnhanced(user);
  const { getTranslation } = useContext(I18nContext);
  const requiredKeyPartiesTranslationKey =
    `Texts.required-key-party-${user.entityType!}-${
      enhanced ? "enhanced" : "basic"
    }` as TranslationKey;
  const updateKeyPartyMutation = useUpdateKeyPartyMutation();
  const onboardingQuery = useGetOnboardingData();
  const keyPartyCommentsByLegalSignerId = useMemo(() => {
    const map = new Map<string, string>();
    for (const { legalSignerId, comment } of onboardingQuery.data
      ?.requestedUpdatesGrouped?.keyPartyComments ?? []) {
      map.set(legalSignerId, comment);
    }
    return map;
  }, [onboardingQuery.data?.requestedUpdatesGrouped?.keyPartyComments]);
  const keyPartiesGeneralComment =
    onboardingQuery.data?.requestedUpdatesGrouped?.keyPartiesGeneralComment;
  const requestedUpdatesRoundId =
    onboardingQuery.data?.requestedUpdatesGrouped?.requestedAt;

  // Pure read, safe to call during render — the actual resolution *write*
  // (markPotentiallyResolved) happens in the effect below, once per commit,
  // rather than inline here on every one of this function's many per-render
  // call sites (once per visible row's card/cells, plus the two dialogs).
  const getKeyPartyFlagState = useCallback(
    (keyParty: KeyParty): { isFlagged: boolean; comment?: string } => {
      const comment = keyPartyCommentsByLegalSignerId.get(keyParty.id);
      if (!comment) {
        return { isFlagged: false };
      }
      return {
        isFlagged: !isItemResolved(
          user.investorId,
          keyParty.id,
          requestedUpdatesRoundId,
        ),
        comment,
      };
    },
    [keyPartyCommentsByLegalSignerId, user.investorId, requestedUpdatesRoundId],
  );

  const [, bumpAfterKeyPartyResolutionChange] = useReducer(
    (c: number) => c + 1,
    0,
  );

  useEffect(() => {
    let anyResolutionChanged = false;
    for (const keyParty of keyParties) {
      if (!keyPartyCommentsByLegalSignerId.has(keyParty.id)) {
        continue;
      }
      const wasResolved = isItemResolved(
        user.investorId,
        keyParty.id,
        requestedUpdatesRoundId,
      );
      const isResolved = markPotentiallyResolved(
        user.investorId,
        keyParty.id,
        editableFieldsSnapshot(keyParty),
        requestedUpdatesRoundId,
      );
      if (isResolved !== wasResolved) {
        anyResolutionChanged = true;
      }
    }
    if (anyResolutionChanged) {
      bumpAfterKeyPartyResolutionChange();
    }
  }, [
    keyParties,
    keyPartyCommentsByLegalSignerId,
    user.investorId,
    requestedUpdatesRoundId,
  ]);

  const queryClient = useQueryClient();
  const [editIndividualKeyPartyInfo, setEditIndividualKeyPartyInfo] = useState<{
    open: boolean;
    kp?: KeyParty;
  }>({ open: false });
  const [editEntityKeyPartyInfo, setEditEntityKeyPartyInfo] = useState<{
    open: boolean;
    kp?: KeyParty;
  }>({ open: false });
  const [editIndividualKeyPartyDetails, setEditIndividualKeyPartyDetails] =
    useState<{
      open: boolean;
      kp?: KeyParty;
    }>({
      open: false,
    });
  const [editEntityKeyPartyDetails, setEditEntityKeyPartyDetails] = useState<{
    open: boolean;
    kp?: KeyParty;
  }>({
    open: false,
  });
  const [deleteKeyParty, setDeleteKeyParty] = useState<{
    open: boolean;
    kp?: KeyParty;
  }>({
    open: false,
  });

  const snackbarContext = useContext(SnackbarContext);

  const incomplete = keyParties.some((kp) => !kp.isComplete);

  const openDetailsDialog = useCallback((kp: KeyParty) => {
    if (kp.signerType === "individual") {
      setEditIndividualKeyPartyDetails({ open: true, kp });
    } else {
      setEditEntityKeyPartyDetails({ open: true, kp });
    }
  }, []);

  const addIndividualCompleteDetailsButtonKey =
    translationKeys.PROFILE_KEY_PARTIES_ADD_INDIVIDUAL_SUCCESS_COMPLETE_DETAILS_BUTTON;

  return (
    <>
      {keyPartiesGeneralComment && (
        <WhmAlert severity="error" sx={{ marginBottom: "20px" }}>
          {keyPartiesGeneralComment}
        </WhmAlert>
      )}
      <WhmStack gap="20px">
        <InfoCard
          title={
            <Translate
              translationKey={translationKeys.PROFILE_KEY_PARTIES_TITLE}
            />
          }
          subtitle={
            <Translate
              translationKey={translationKeys.PROFILE_KEY_PARTIES_SUBTITLE}
            />
          }
          action={
            keyParties.length === 0 || isReadOnly ? undefined : (
              <AddMemberButton
                onAddIndividualClick={() =>
                  setEditIndividualKeyPartyInfo({ open: true })
                }
                onAddEntityClick={() =>
                  setEditEntityKeyPartyInfo({ open: true })
                }
              />
            )
          }
          smallPadding
        >
          <WhmStack gap="16px">
            {!isReadOnly && (
              <WhmAlert icon={false} severity="info">
                <TranslateHtml
                  translationKey={translationKeys.PROFILE_KEY_PARTIES_REQUIRED}
                  replacements={{
                    parties: getTranslation(requiredKeyPartiesTranslationKey),
                    entityType: getTranslation(
                      entityTypesTranslations[user.entityType!],
                    ),
                  }}
                />
              </WhmAlert>
            )}
            {!isReadOnly && incomplete && (
              <WhmTypography variant="body2" sx={{ color: "error.main" }}>
                <Translate
                  translationKey={
                    translationKeys.PROFILE_KEY_PARTIES_INCOMPLETE_ALERT
                  }
                />
              </WhmTypography>
            )}
            <WhmFlexDataGrid
              clientSide
              rowsPerPageOptions={[10, 25, 50]}
              initialPageSize={10}
              initialPage={0}
              columnsVisibility={{}}
              disableColumnSorting
              disableColumnResize
              disableColumnFilter
              disableColumnSelector
              disableColumnMenu
              disableRowSelectionOnClick
              rowSelection={false}
              rows={keyParties.map((party) => ({
                ...party,
              }))}
              sx={{
                "& .MuiDataGrid-virtualScrollerContent": { minHeight: "unset" },
                "--DataGrid-overlayHeight": "300px",
                minHeight: "500px",
                border: "none",
                "& .MuiDataGrid-columnHeader:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
              }}
              slotProps={{
                pagination: {
                  slotProps: {
                    select: { sx: { minWidth: "unset" } },
                  },
                },
              }}
              CardComponent={useCallback(
                ({ row }: { row: KeyParty }) => {
                  const flagState = getKeyPartyFlagState(row);
                  return (
                    <StyledCard>
                      <WhmStack width="100%" gap="16px">
                        <WhmStack
                          direction="row"
                          alignItems="center"
                          gap="16px"
                          justifyContent="space-between"
                        >
                          <WhmStack
                            direction="row"
                            alignItems="center"
                            gap="8px"
                            overflow="hidden"
                          >
                            <WhmAvatar
                              sx={{
                                width: 32,
                                height: 32,
                                backgroundColor: "#e0e0e0",
                              }}
                            >
                              {row.signerType === "individual" && (
                                <IndividualIcon fill="black" />
                              )}
                              {row.signerType === "entity" && (
                                <EntityIcon fill="black" />
                              )}
                            </WhmAvatar>
                            <WhmStack overflow="hidden">
                              <WhmTypography
                                variant="body2"
                                sx={{
                                  textOverflow: "ellipsis",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row.displayName}
                              </WhmTypography>
                              {row.email && (
                                <WhmTypography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    textOverflow: "ellipsis",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.email}
                                </WhmTypography>
                              )}
                            </WhmStack>
                          </WhmStack>
                          <MoreActionsMenu
                            disabled={
                              updateKeyPartyMutation.isPending || isRefreshing
                            }
                            isComplete={row.isComplete}
                            isReadOnly={isReadOnly}
                            canDelete={canDelete}
                            onDelete={() => {
                              setDeleteKeyParty({
                                open: true,
                                kp: row,
                              });
                            }}
                            onEditDetails={() => openDetailsDialog(row)}
                            onEditInformation={() => {
                              if (row.signerType === "individual") {
                                setEditIndividualKeyPartyInfo({
                                  open: true,
                                  kp: row,
                                });
                              }

                              if (row.signerType === "entity") {
                                setEditEntityKeyPartyInfo({
                                  open: true,
                                  kp: row,
                                });
                              }
                            }}
                          />
                        </WhmStack>

                        {!isReadOnly && flagState.isFlagged && (
                          <WhmButton
                            variant="contained"
                            color="error"
                            sx={{ width: "fit-content" }}
                            data-test-id={`resolve-update-button-${row.id}`}
                            disabled={
                              updateKeyPartyMutation.isPending || isRefreshing
                            }
                            onClick={() => openDetailsDialog(row)}
                          >
                            <Translate
                              translationKey={
                                translationKeys.PROFILE_KEY_PARTIES_RESOLVE_UPDATE
                              }
                            />
                          </WhmButton>
                        )}

                        {!isReadOnly &&
                          !row.isComplete &&
                          !flagState.isFlagged && (
                            <WhmButton
                              variant="contained"
                              sx={{ width: "fit-content" }}
                              data-test-id={`continue-button-${row.id}`}
                              disabled={
                                updateKeyPartyMutation.isPending || isRefreshing
                              }
                              onClick={() => openDetailsDialog(row)}
                            >
                              <WhmStack
                                direction="row"
                                alignItems="center"
                                gap="4px"
                              >
                                <WhmTooltip
                                  title={
                                    <Translate
                                      translationKey={
                                        translationKeys.PROFILE_KEY_PARTIES_ADDITIONAL_DETAILS_TOOLTIP
                                      }
                                    />
                                  }
                                >
                                  <Warning
                                    style={iconSmall}
                                    fill="currentColor"
                                  />
                                </WhmTooltip>
                                <Translate
                                  translationKey={
                                    translationKeys.PROFILE_KEY_PARTIES_ADD_INDIVIDUAL_SUCCESS_COMPLETE_DETAILS_BUTTON
                                  }
                                />
                              </WhmStack>
                            </WhmButton>
                          )}
                      </WhmStack>
                    </StyledCard>
                  );
                },
                [
                  getKeyPartyFlagState,
                  updateKeyPartyMutation.isPending,
                  isRefreshing,
                  isReadOnly,
                  canDelete,
                  openDetailsDialog,
                ],
              )}
              columns={useMemo(
                () => [
                  {
                    field: "displayName",
                    flex: 1,
                    headerName: getTranslation(
                      translationKeys.PROFILE_KEY_PARTIES_MEMBER_NAME_COLUMN,
                    ),
                    renderCell: ({ row }) => (
                      <WhmStack
                        direction="row"
                        alignItems="center"
                        gap="8px"
                        height="100%"
                      >
                        <WhmAvatar
                          sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: "#e0e0e0",
                          }}
                        >
                          {(row as KeyParty).signerType === "individual" && (
                            <IndividualIcon fill="black" />
                          )}
                          {(row as KeyParty).signerType === "entity" && (
                            <EntityIcon fill="black" />
                          )}
                        </WhmAvatar>
                        <WhmStack overflow="hidden" justifyContent="center">
                          <WhmTypography
                            variant="body2"
                            sx={{
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {(row as KeyParty).displayName}
                          </WhmTypography>
                          {(row as KeyParty).email && (
                            <WhmTypography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {(row as KeyParty).email}
                            </WhmTypography>
                          )}
                        </WhmStack>
                      </WhmStack>
                    ),
                  },
                  {
                    field: "uboOwning",
                    flex: 1,
                    renderHeader: () => (
                      <WhmStack
                        direction="row"
                        alignItems="center"
                        gap="4px"
                        height="100%"
                      >
                        {enhanced ? (
                          <WhmStack
                            direction="row"
                            alignItems="center"
                            gap="4px"
                            overflow="hidden"
                          >
                            <WhmTypography
                              variant="subtitle2"
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              <Translate
                                translationKey={
                                  translationKeys.PROFILE_KEY_PARTIES_10_PERCENT_OWNERSHIP_COLUMN
                                }
                              />
                            </WhmTypography>
                            <WhmTooltip
                              title={
                                <Translate
                                  translationKey={
                                    translationKeys.PROFILE_KEY_PARTIES_PERCENT_OWNERSHIP_COLUMN_TOOLTIP
                                  }
                                />
                              }
                            >
                              <InfoOutlined
                                style={iconSmall}
                                fill={whmTheme.palette.action.active}
                              />
                            </WhmTooltip>
                          </WhmStack>
                        ) : (
                          <WhmStack
                            direction="row"
                            alignItems="center"
                            gap="4px"
                            overflow="hidden"
                          >
                            <WhmTypography
                              variant="subtitle2"
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              <Translate
                                translationKey={
                                  translationKeys.PROFILE_KEY_PARTIES_25_PERCENT_OWNERSHIP_COLUMN
                                }
                              />
                            </WhmTypography>
                            <WhmTooltip
                              title={
                                <Translate
                                  translationKey={
                                    translationKeys.PROFILE_KEY_PARTIES_PERCENT_OWNERSHIP_COLUMN_TOOLTIP
                                  }
                                />
                              }
                            >
                              <InfoOutlined
                                style={iconSmall}
                                fill={whmTheme.palette.action.active}
                              />
                            </WhmTooltip>
                          </WhmStack>
                        )}
                      </WhmStack>
                    ),
                    renderCell: (params) => (
                      <WhmStack
                        direction="row"
                        alignItems="center"
                        gap="4px"
                        height="100%"
                      >
                        <WhmFormControlLabel
                          sx={{ margin: 0 }}
                          control={
                            <WhmCheckbox
                              data-test-id={`ownership-checkbox-${(params.row as KeyParty).id}`}
                              disabled={
                                isReadOnly ||
                                updateKeyPartyMutation.isPending ||
                                isRefreshing
                              }
                              checked={
                                (params.row as KeyParty).uboOwning !== "none"
                              }
                              onClick={() => {
                                updateKeyPartyMutation.mutate(
                                  {
                                    keyPartyId: (params.row as KeyParty).id,
                                    keyPartyData: {
                                      uboOwning:
                                        (params.row as KeyParty).uboOwning !==
                                        "none"
                                          ? "none"
                                          : enhanced
                                            ? "10+"
                                            : "25+",
                                    },
                                  },
                                  {
                                    onSuccess: () => {
                                      void invalidateKeyPartiesQueries(
                                        queryClient,
                                      );
                                    },
                                    onError: (e) => {
                                      snackbarContext.showSnackbar({
                                        message: <ErrorSnackbar error={e} />,
                                        severity: "error",
                                      });
                                    },
                                  },
                                );
                              }}
                            />
                          }
                          label={<WhmTypography>Yes</WhmTypography>}
                        />
                      </WhmStack>
                    ),
                  },
                  {
                    field: "isLegalSigner",
                    flex: 1,
                    renderHeader: () => (
                      <WhmStack
                        direction="row"
                        alignItems="center"
                        gap="4px"
                        overflow="hidden"
                        height="100%"
                      >
                        <WhmTypography
                          variant="subtitle2"
                          sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                          <Translate
                            translationKey={
                              translationKeys.PROFILE_KEY_PARTIES_LEGAL_SIGNER_COLUMN
                            }
                          />
                        </WhmTypography>
                        <WhmTooltip
                          title={
                            <Translate
                              translationKey={
                                translationKeys.PROFILE_KEY_PARTIES_LEGAL_SIGNER_COLUMN_TOOLTIP
                              }
                            />
                          }
                        >
                          <InfoOutlined
                            style={iconSmall}
                            fill={whmTheme.palette.action.active}
                          />
                        </WhmTooltip>
                      </WhmStack>
                    ),
                    renderCell: (params) => (
                      <WhmStack
                        direction="row"
                        alignItems="center"
                        gap="4px"
                        height="100%"
                      >
                        {(params.row as KeyParty).signerType ===
                          "individual" && (
                          <WhmFormControlLabel
                            sx={{ margin: 0 }}
                            control={
                              <WhmCheckbox
                                data-test-id={`legal-signer-checkbox-${(params.row as KeyParty).id}`}
                                disabled={
                                  isReadOnly ||
                                  updateKeyPartyMutation.isPending ||
                                  isRefreshing
                                }
                                checked={(params.row as KeyParty).isLegalSigner}
                                onClick={() => {
                                  updateKeyPartyMutation.mutate(
                                    {
                                      keyPartyId: (params.row as KeyParty).id,
                                      keyPartyData: {
                                        isLegalSigner: !(params.row as KeyParty)
                                          .isLegalSigner,
                                      },
                                    },
                                    {
                                      onSuccess: () => {
                                        void invalidateKeyPartiesQueries(
                                          queryClient,
                                        );
                                      },
                                      onError: (e) => {
                                        snackbarContext.showSnackbar({
                                          message: <ErrorSnackbar error={e} />,
                                          severity: "error",
                                        });
                                      },
                                    },
                                  );
                                }}
                              />
                            }
                            label={<WhmTypography>Yes</WhmTypography>}
                          />
                        )}
                        {(params.row as KeyParty).signerType === "entity" && (
                          <WhmStack
                            direction="row"
                            alignItems="center"
                            gap="4px"
                          >
                            <WhmTypography sx={{ marginLeft: "10px" }}>
                              <Translate
                                translationKey={
                                  translationKeys.PROFILE_KEY_PARTIES_NOT_APPLICABLE
                                }
                              />
                            </WhmTypography>
                            <WhmTooltip
                              title={
                                <Translate
                                  translationKey={
                                    translationKeys.PROFILE_KEY_PARTIES_PERCENT_OWNERSHIP_COLUMN_ENTITY_TOOLTIP
                                  }
                                />
                              }
                            >
                              <InfoOutlined
                                style={iconSmall}
                                fill={whmTheme.palette.action.active}
                              />
                            </WhmTooltip>
                          </WhmStack>
                        )}
                      </WhmStack>
                    ),
                  },
                  {
                    field: "actions",
                    flex: 1,
                    filterable: false,
                    editable: false,
                    headerName: "",
                    renderCell: (params) => {
                      const row = params.row as KeyParty;
                      const flagState = getKeyPartyFlagState(row);
                      return (
                        <>
                          <WhmStack
                            direction="row"
                            alignItems="center"
                            gap="8px"
                            justifyContent="end"
                            height="100%"
                          >
                            {!isReadOnly && flagState.isFlagged && (
                              <WhmButton
                                variant="contained"
                                color="error"
                                size="small"
                                data-test-id={`resolve-update-button-${row.id}`}
                                disabled={
                                  updateKeyPartyMutation.isPending ||
                                  isRefreshing
                                }
                                onClick={() => openDetailsDialog(row)}
                              >
                                <Translate
                                  translationKey={
                                    translationKeys.PROFILE_KEY_PARTIES_RESOLVE_UPDATE
                                  }
                                />
                              </WhmButton>
                            )}
                            {!isReadOnly &&
                              !row.isComplete &&
                              !flagState.isFlagged && (
                                <WhmButton
                                  variant="contained"
                                  size="small"
                                  data-test-id={`continue-button-${row.id}`}
                                  disabled={
                                    updateKeyPartyMutation.isPending ||
                                    isRefreshing
                                  }
                                  onClick={() => openDetailsDialog(row)}
                                >
                                  <WhmTooltip
                                    title={
                                      <Translate
                                        translationKey={
                                          translationKeys.PROFILE_KEY_PARTIES_ADDITIONAL_DETAILS_TOOLTIP
                                        }
                                      />
                                    }
                                  >
                                    <WhmStack
                                      direction="row"
                                      alignItems="center"
                                      gap="4px"
                                    >
                                      <Warning
                                        style={iconSmall}
                                        fill="currentColor"
                                      />
                                      <Translate
                                        translationKey={
                                          addIndividualCompleteDetailsButtonKey
                                        }
                                      />
                                    </WhmStack>
                                  </WhmTooltip>
                                </WhmButton>
                              )}
                            <MoreActionsMenu
                              disabled={
                                updateKeyPartyMutation.isPending || isRefreshing
                              }
                              isComplete={row.isComplete}
                              isReadOnly={isReadOnly}
                              canDelete={canDelete}
                              onDelete={() => {
                                setDeleteKeyParty({
                                  open: true,
                                  kp: row,
                                });
                              }}
                              onEditDetails={() => openDetailsDialog(row)}
                              onEditInformation={() => {
                                if (row.signerType === "individual") {
                                  setEditIndividualKeyPartyInfo({
                                    open: true,
                                    kp: row,
                                  });
                                }
                                if (row.signerType === "entity") {
                                  setEditEntityKeyPartyInfo({
                                    open: true,
                                    kp: row,
                                  });
                                }
                              }}
                            />
                          </WhmStack>
                        </>
                      );
                    },
                    sortable: false,
                  },
                ],
                [
                  getTranslation,
                  enhanced,
                  isReadOnly,
                  updateKeyPartyMutation,
                  isRefreshing,
                  getKeyPartyFlagState,
                  queryClient,
                  snackbarContext,
                  canDelete,
                  openDetailsDialog,
                  addIndividualCompleteDetailsButtonKey,
                ],
              )}
              slots={{
                noRowsOverlay: isReadOnly
                  ? undefined
                  : EmptyKeyPartiesOverlay({
                      onAddIndividualClick: () =>
                        setEditIndividualKeyPartyInfo({ open: true }),
                      onAddEntityClick: () =>
                        setEditEntityKeyPartyInfo({ open: true }),
                    }),
              }}
            />
          </WhmStack>
        </InfoCard>
        {isReadOnly ? (
          <WhmTypography variant="body2" sx={{ color: "text.secondary" }}>
            <TranslateHtml
              translationKey={
                translationKeys.PROFILE_KEY_PARTIES_READ_ONLY_UPDATE_FOOTER
              }
            />
          </WhmTypography>
        ) : (
          <WhmStack gap="8px">
            <WhmLoadingButton
              size="large"
              loading={updateKeyPartyMutation.isPending}
              sx={{
                width: { xs: "100%", xl: "fit-content" },
                svg: {
                  display: "block !important",
                },
                "&.MuiLoadingButton-loading": {
                  color: "black",
                  opacity: "0.3",
                  span: {
                    color: "black",
                  },
                },
              }}
              variant="contained"
              data-test-id="save-key-parties-button"
            >
              <Translate
                translationKey={translationKeys.PROFILE_KEY_PARTIES_SAVE}
              />
            </WhmLoadingButton>
            <WhmTypography variant="body2" sx={{ color: "text.secondary" }}>
              <TranslateHtml
                translationKey={translationKeys.PROFILE_KEY_PARTIES_SAVE_NOTE}
              />
            </WhmTypography>
          </WhmStack>
        )}
      </WhmStack>
      <AddIndividualDialog
        open={editIndividualKeyPartyInfo.open}
        keyParty={editIndividualKeyPartyInfo.kp}
        readOnly={isReadOnly}
        onClose={() =>
          setEditIndividualKeyPartyInfo((kp) => ({ ...kp, open: false }))
        }
        openCompleteDetails={(kp) =>
          setEditIndividualKeyPartyDetails({ kp, open: true })
        }
      />
      <CompleteIndividualDialog
        open={editIndividualKeyPartyDetails.open}
        keyParty={editIndividualKeyPartyDetails.kp}
        readOnly={isReadOnly}
        flaggedComment={
          editIndividualKeyPartyDetails.kp
            ? getKeyPartyFlagState(editIndividualKeyPartyDetails.kp).comment
            : undefined
        }
        onClose={() =>
          setEditIndividualKeyPartyDetails((kp) => ({ ...kp, open: false }))
        }
      />
      <CompleteEntityDialog
        open={editEntityKeyPartyDetails.open}
        keyParty={editEntityKeyPartyDetails.kp}
        readOnly={isReadOnly}
        flaggedComment={
          editEntityKeyPartyDetails.kp
            ? getKeyPartyFlagState(editEntityKeyPartyDetails.kp).comment
            : undefined
        }
        onClose={() =>
          setEditEntityKeyPartyDetails((kp) => ({ ...kp, open: false }))
        }
      />
      <AddEntityDialog
        open={editEntityKeyPartyInfo.open}
        keyParty={editEntityKeyPartyInfo.kp}
        readOnly={isReadOnly}
        onClose={() =>
          setEditEntityKeyPartyInfo((kp) => ({ ...kp, open: false }))
        }
        openCompleteDetails={(kp) =>
          setEditEntityKeyPartyDetails({ kp, open: true })
        }
      />
      <DeleteKeyPartyDialog
        open={deleteKeyParty.open}
        keyParty={deleteKeyParty.kp}
        onClose={() => setDeleteKeyParty((kp) => ({ ...kp, open: false }))}
      />
    </>
  );
}

function MoreActionsMenu({
  isComplete,
  onEditDetails,
  onEditInformation,
  onDelete,
  disabled,
  isReadOnly = false,
  canDelete = true,
}: {
  isComplete: boolean;
  onEditDetails: () => void;
  onEditInformation: () => void;
  onDelete: () => void;
  disabled: boolean;
  isReadOnly?: boolean;
  canDelete?: boolean;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const canDeleteKeyParty = !isReadOnly && canDelete;

  return (
    <>
      <WhmIconButton
        disabled={disabled}
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          setMenuOpen(true);
        }}
      >
        <Dots />
      </WhmIconButton>
      <FlexMenu
        open={menuOpen}
        anchorEl={anchorEl}
        onClose={() => setMenuOpen(false)}
      >
        <WhmMenuItem
          onClick={() => {
            setMenuOpen(false);
            onEditDetails();
          }}
        >
          <WhmStack>
            <WhmTypography variant="body2">
              {isComplete || isReadOnly ? (
                <Translate
                  translationKey={
                    translationKeys.PROFILE_KEY_PARTIES_VIEW_DETAILS
                  }
                />
              ) : (
                <Translate
                  translationKey={
                    translationKeys.PROFILE_KEY_PARTIES_COMPLETE_DETAILS
                  }
                />
              )}
            </WhmTypography>
            <WhmTypography variant="body2" sx={{ color: "text.secondary" }}>
              {isComplete || isReadOnly ? (
                <Translate
                  translationKey={
                    translationKeys.PROFILE_KEY_PARTIES_VIEW_DETAILS_SUBTITLE
                  }
                />
              ) : (
                <Translate
                  translationKey={
                    translationKeys.PROFILE_KEY_PARTIES_COMPLETE_DETAILS_MENU_SUBTITLE
                  }
                />
              )}
            </WhmTypography>
          </WhmStack>
        </WhmMenuItem>
        <WhmMenuItem
          onClick={() => {
            setMenuOpen(false);
            onEditInformation();
          }}
        >
          <WhmStack>
            <WhmTypography variant="body2">
              <Translate
                translationKey={
                  translationKeys.PROFILE_KEY_PARTIES_VIEW_MEMBER_INFO
                }
              />
            </WhmTypography>
            <WhmTypography variant="body2" sx={{ color: "text.secondary" }}>
              <Translate
                translationKey={
                  translationKeys.PROFILE_KEY_PARTIES_VIEW_MEMBER_INFO_DETAILS
                }
              />
            </WhmTypography>
          </WhmStack>
        </WhmMenuItem>
        {canDeleteKeyParty && [
          <WhmDivider key="divider" />,
          <WhmMenuItem
            onClick={() => {
              setMenuOpen(false);
              onDelete();
            }}
            key="delete"
          >
            <WhmStack direction="row" gap="8px" alignItems="center">
              <DeleteIcon fill={whmTheme.palette.error.main} />
              <WhmStack>
                <WhmTypography variant="body2" sx={{ color: "error.main" }}>
                  <Translate
                    translationKey={
                      translationKeys.PROFILE_KEY_PARTIES_VIEW_MEMBER_REMOVE
                    }
                  />
                </WhmTypography>
                <WhmTypography variant="body2" sx={{ color: "text.secondary" }}>
                  <Translate
                    translationKey={
                      translationKeys.PROFILE_KEY_PARTIES_VIEW_MEMBER_REMOVE_DETAILS
                    }
                  />
                </WhmTypography>
              </WhmStack>
            </WhmStack>
          </WhmMenuItem>,
        ]}
      </FlexMenu>
    </>
  );
}
